import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { getKBContent } from "@/data/knowledgeBaseContent";
// Icons removed - text-only UI

// Legacy v1.0 content kept as fallback
const KNOWLEDGE_BASE_CONTENT_LEGACY = `
# AIRLINE OPS: KNOWLEDGE BASE
## Player Guide v1.0

---

# WELCOME

Airline Ops is a realistic airline management simulator. You'll build routes, manage crews, negotiate for airport slots, and grow from a single-hub regional operator to a global airline empire.

This guide explains how everything works. No secrets, no hidden mechanics -just the information you need to make smart decisions.

---

# TABLE OF CONTENTS

1. Getting Started
2. The Daily Loop
3. Your Fleet
4. Routes & Scheduling
5. **Simulation Mode Operations**
6. Airport Slots
7. Crew Management
8. Reputation & Tiers
9. Relations
10. Weather
11. Technology Systems
12. Finances
13. Elite Operator Status (EOS)
14. The Gold A380 & Collectibles
15. Collectible Tiers
16. Tips & Strategy

---

# GETTING STARTED

## Your Starting Position

You begin with:
- **$40,000,000** in cash
- **Zero aircraft** (you must lease)
- **KPDX (Portland International)** as your hub
- **Starting slot allocation** at KPDX only
- **Tier 1** reputation

## First Steps

1. **Lease aircraft** -Start with E175 regional jets. They're affordable and right-sized for building initial routes.

2. **Build regional routes** -Connect KPDX to nearby airports. Prove demand before expanding.

3. **Watch your cash** -$40M sounds like a lot. It isn't. Lease payments, fuel, crew, and airport fees add up fast.

4. **Don't buy aircraft yet** -Leasing preserves cash. Buying early is a trap that leads to bankruptcy.

---

# THE DAILY LOOP

## What Happens Each Day

The game runs in real-time (with optional time compression). Here's the rhythm:

**Throughout the day:**
- Flights depart and arrive
- Weather affects operations
- Disruptions occur (mechanical, crew, weather)
- Revenue accumulates

**At 22:00 (game time):**
- End-of-day processing
- Slot negotiations resolve
- Daily financial summary

**At 06:00 (game time):**
- EOS qualification check (if applicable)

## Your Daily Tasks

1. Check overnight performance
2. Review any alerts (crew, weather, mechanical)
3. Adjust schedules if needed
4. Monitor live flights in Ops Center
5. Handle disruptions as they occur
6. Plan expansion (new routes, slot bids)

---

# YOUR FLEET

## Aircraft Types

### Regional
- **E175** -Your starter workhorse. Affordable, reliable, essential.
- **CRJ-900** -Alternative regional option.

### Narrowbody
- **737 MAX 8** -Domestic backbone.
- **A320neo** -European operations.
- **A321XLR** -Long thin routes.

### Widebody
- **787-9** -Efficient long-haul.
- **A350-900** -Premium long-haul.
- **777-300ER** -High-capacity routes.

## Lease vs. Buy

**Leasing:**
- Preserves cash
- Lower commitment
- Flexibility to scale up or down
- Recommended for early/mid game

**Buying:**
- Builds equity
- No ongoing lease payments
- Locks up capital
- Only consider when cash-rich

## Aircraft Stats Matter

Each aircraft has real stats that affect gameplay:
- **Speed** -Determines flight duration
- **Range** -Determines which routes are possible
- **Capacity** -Maximum passengers per flight

**Warning:** If you schedule a flight beyond an aircraft's range, you'll get a warning. If you proceed anyway, the aircraft will run out of fuel and make an emergency landing -with severe financial, reputational, and regulatory consequences.

## Never Sell Your E175s

Your starter E175s become **Crew Rail** shuttles later in the game. They operate 24/7 between crew bases, repositioning crew to prevent legality violations. They're infrastructure, not disposable assets.

---

# ROUTES & SCHEDULING

## Route Economics

Every route has:
- **Demand** -How many people want to fly it (varies by day, season, events)
- **Competition** -Other airlines affect your load factors
- **Distance** -Affects fuel cost, crew duty time, aircraft utilization

## Building Good Schedules

- **Bank departures** at your hub for connections
- **Consider crew rotations** -Crews need rest
- **Build in buffers** -Tight schedules cascade when disrupted
- **Match aircraft to demand** -Don't fly a 777 on a route that needs an E175

## Utilization

Aircraft on the ground lose money. Maximize flight hours while respecting:
- Maintenance requirements
- Crew legality
- Realistic turnaround times

---

# SIMULATION MODE OPERATIONS

Simulation mode is the full airline operations experience. Every system connects -crew legality affects scheduling, weather affects fuel, fuel affects weight, weight affects dispatch. Master this stack and you master the game.

## The Preflight Stack

Before every departure, you complete a 6-task preflight checklist. Each task validates a critical system:

| Task | What It Checks | Can Block Dispatch? |
|------|----------------|---------------------|
| 1. Crew Briefing | Flight plan, weather awareness | No |
| 2. Weather Check | Origin/destination conditions | No (advisory) |
| 3. Fuel Calculations | Fuel load vs. requirements | No |
| 4. Load Sheet (W&B) | Weight vs. aircraft limits | **YES** |
| 5. NOTAMs | Airfield/airspace notices | No |
| 6. ATC Clearance | Departure authorization | **YES** (must readback) |

**Shortcut:** The "Autocomplete All" button skips the checklist but costs **-2 reputation**. Use sparingly.

---

## Fuel Planning

### The Fuel Breakdown

Every flight requires fuel for multiple purposes:

| Component | Calculation | Purpose |
|-----------|-------------|---------|
| **Trip Fuel** | Burn rate x flight hours | Gets you there |
| **Taxi Fuel** | 200 kg (fixed) | Taxi-out and taxi-in |
| **Contingency** | 5% of trip fuel | Headwinds, routing changes |
| **Alternate Fuel** | 45 min at cruise burn | Diversion to alternate airport |
| **Reserve Fuel** | 30 min at holding burn | Regulatory minimum |
| **Weather Extra** | +25% if IFR/LIFR | Bad weather buffer |

### Aircraft Burn Rates

Different aircraft burn fuel at vastly different rates:

| Category | Example | Burn Rate |
|----------|---------|-----------|
| Regional | E175 | ~1,900 kg/hr |
| Narrowbody | A320 | ~2,700 kg/hr |
| Widebody | 787 | ~5,800 kg/hr |
| Heavy | 747-8 | ~10,200 kg/hr |

### Weather-Aware Fueling

The system automatically adds 25% extra fuel when your destination reports:
- **IFR** (Instrument Flight Rules) -Low visibility/ceilings
- **LIFR** (Low IFR) -Very low visibility/ceilings

This covers holds, missed approaches, and diversions.

---

## Weight & Balance

### The Weight Chain

| Step | Calculation |
|------|-------------|
| OEW | Empty Aircraft |
| + Passengers | 185 lbs each with bags |
| + Cargo | Forward hold |
| = **ZFW** | Zero Fuel Weight |
| + Fuel | In lbs |
| = **TOW** | Takeoff Weight (must be under MTOW) |
| - Trip Fuel | Burn during flight |
| = **LW** | Landing Weight (must be under MLW) |

### Weight Limits

Every aircraft has hard limits:
- **MTOW** -Maximum Takeoff Weight (structural limit)
- **MLW** -Maximum Landing Weight (gear/structure limit)

**Overweight flights cannot dispatch.** The system blocks you.

### Fixing Overweight

If TOW > MTOW:
1. Reduce passengers (offload some pax)
2. Reduce cargo (leave freight behind)
3. Reduce fuel (only if you have margin)

Option 3 is dangerous -don't cut into reserves.

---

## Weather Categories

Weather reports use FAA flight categories:

| Category | Ceiling | Visibility | Color |
|----------|---------|------------|-------|
| **VFR** | > 3,000 ft | > 5 miles | Green |
| **MVFR** | 1,000-3,000 ft | 3-5 miles | Blue |
| **IFR** | 500-1,000 ft | 1-3 miles | Red |
| **LIFR** | < 500 ft | < 1 mile | Magenta |

### What Weather Affects

- **Fuel planning** -IFR/LIFR adds 25% weather extra
- **Delays** -Bad weather causes ground stops
- **Crew** -Weather delays can push crew to timeout
- **Cascades** -One weather delay affects everything downstream

---

## ATC Communications

### The Clearance

When you open the ATC Clearance task, you receive:
- **Callsign** -Your flight number
- **Routing** -Departure procedure
- **Altitude** -Initial climb altitude
- **Squawk** -4-digit transponder code
- **Frequency** -Departure control freq

### Audio System

The game includes **291 real ATC audio clips**:
- Airport-specific clearances (171 airports)
- Phonetic alphabet (A-Z, 0-9)
- Standard phrases ("cleared for takeoff", "radar contact")
- Altitude instructions
- Frequency handoffs

Audio plays automatically when you open the clearance. Click **"Key Mic"** to transmit your readback.

### The Readback

You must acknowledge the clearance before dispatch. This simulates the real pilot readback procedure. Click the readback button, hear confirmation, then you can dispatch.

---

## NOTAMs (Notices to Airmen)

NOTAMs alert you to:
- **Runway closures** -Construction, maintenance
- **Navigation aids** -VOR/ILS outages
- **Obstacles** -Cranes, temporary structures
- **Airspace** -TFRs, restricted areas
- **Wildlife** -Bird activity warnings
- **Surface** -Taxiway conditions

NOTAMs are generated for both origin and destination. They don't block dispatch but inform your decisions.

---

## Crew Legality (FAA Part 117)

### The Limits

Real FAA regulations govern crew:

| Rule | Limit | Consequence |
|------|-------|-------------|
| **Duty Time** | 14 hours max | Flight blocked |
| **Flight Time** | 8-9 hours/day | Warning ->blocked |
| **Rest Required** | 10 hours minimum | Crew unavailable |
| **28-Day Limit** | 100 flight hours | Crew grounded |

### Fatigue Levels

Crew fatigue is tracked on a 5-level scale:

| Level | Name | Effect |
|-------|------|--------|
| 0 | Rested | Normal operations |
| 1 | Normal | Normal operations |
| 2 | Stressed | Warning (yellow) |
| 3 | Fatigued | Warning (red) |
| 4 | **UNSAFE** | **Flight blocked** |

### What Increases Fatigue

- Long duty periods (+1 at 8h, +1 at 12h)
- Night operations (23:00-06:00)
- Consecutive duty days (+1 per 3 days)
- Short rest periods
- Timezone changes (jet lag)

### Crew Violations

If crew goes illegal:
- Flight cannot depart
- **-60 reputation** penalty
- Must find replacement crew
- Delays cascade through schedule

---

## How It All Connects

This is why Simulation mode is hard:

**The Cascade:**
- Weather delays ->Crew duty time increases ->Crew goes illegal
- Flight cancelled ->Reputation damage ->Slot negotiations harder
- Fewer slots ->Less revenue ->Can't afford CrewWise
- More crew violations ->More cancellations ->**Death spiral**

**The solution:** Plan buffers, watch weather, buy CrewWise early, use Crew Rail.

---

## Simple Mode vs Simulation Mode

| Aspect | Simple Mode | Simulation Mode |
|--------|-------------|-----------------|
| Preflight | Auto-complete | 6-task checklist |
| Crew | Automatic | Manual FAA compliance |
| Fuel | Calculated | Manual planning |
| ATC | Silent | Audio clearances |
| Time Compression | Up to 4x (12x with EOS) | Up to 4x (12x with EOS) |
| Aircraft | Purchase only | Purchase or lease |
| EOS Requirement | 10 days | 10 days |
| Mythic Tier | No access | **Exclusive access** |
| Difficulty | Accessible | Extraordinarily realistic (well, not counting the rolls) |

---

# AIRPORT SLOTS

## How Slots Work

Every departure requires a slot at the origin airport. Slots are limited and valuable.

## Getting Slots

You expand outward from your hub through **slot negotiations**. These happen nightly at 22:00.

Success depends on:
- Your reputation tier
- Existing presence at nearby airports
- Competition from other airlines
- Airport congestion

## Geographic Expansion

You can't jump from Portland to Singapore on day one. Expansion is concentric:

1. Win slots at nearby airports (Seattle, Boise)
2. Expand to medium-distance airports (Denver, San Francisco)
3. Reach major hubs (JFK, LAX)
4. Go international (London, Dubai, Singapore)

## Competitive Slots

Some airports are highly competitive:
- **JFK, LAX, LHR** -Everyone wants in
- **Smaller regionals** -Easier entry points

Build your network strategically. Sometimes the indirect path is faster.

---

# CREW MANAGEMENT

## FAA Regulations

Crew legality is based on real FAA regulations:
- Flight time limits
- Duty time limits
- Required rest periods

These are not suggestions. Violations ground your operation.

## What Happens When Crew Go Illegal

If a crew reaches their limit:
- Flight cannot depart
- Scramble to find replacement crew
- Delays cascade through your schedule
- Reputation damage

## CrewWise Technology

For $18,000,000, you can purchase **CrewWise** -an AI system that predicts crew legality violations 2-6 hours before they happen.

With CrewWise, you can:
- Reassign crew proactively
- Activate Crew Rail shuttles
- Adjust schedules before problems hit

Without it, you find out a crew is illegal when they're supposed to fly.

## Crew Rail

Your E175s can become **Crew Rail** shuttles -operating 24/7 between crew bases to reposition crew and prevent illegality. This is why you never sell your starter aircraft.

---

# REPUTATION & TIERS

## The Tier System

| Tier | Name | Reputation Required |
|------|------|---------------------|
| 1 | Startup | 0 |
| 2 | Regional | 3,000 |
| 3 | National | 7,000 |
| 4 | International | 12,000 |
| 5 | Elite | 15,000 |

## What Affects Reputation

**Positive:**
- On-time performance
- Customer satisfaction
- Safety record
- Network growth
- Financial stability

**Negative:**
- Delays and cancellations
- Safety incidents
- Poor service
- Financial distress

## Why Tiers Matter

Higher tiers unlock:
- Better slot negotiation odds
- Technology discounts
- Access to premium routes
- Eventually: EOS eligibility

---

# RELATIONS

## Two Types of Relations

### Airport Relations
Your relationship with each airport affects:
- Slot negotiation success
- Gate availability
- Ground handling costs

Build relations by:
- Operating reliably at the airport
- On-time performance
- Not cancelling flights

### Competitor Relations
Your relationship with other airlines affects:
- Codeshare opportunities
- Competitive dynamics
- Market behavior

---

# WEATHER

## Real-Time Weather

Weather is pulled from real-world data and affects your operations.

## Weather Effects

| Condition | Effect |
|-----------|--------|
| Light winds (< 25 kts) | Normal operations |
| Moderate winds (25-35 kts) | 15 minute delays |
| High winds (35-45 kts) | 30 minute delays |
| Severe winds (45+ kts) | **Ground stop** |
| Low visibility (1-3 mi) | 15 minute delays |
| Very low visibility (< 1 mi) | 45 minute delays |
| Zero visibility (< 0.25 mi) | **Ground stop** |
| Heavy precipitation | 30 minute delays |
| Thunderstorms | **Ground stop** |

## Weather Radar

The map shows a real-time precipitation radar overlay. Use it to:
- See storms approaching your routes
- Anticipate delays before they hit
- Make proactive schedule adjustments

## Cascading Effects

Weather delays feed into everything else. A 45-minute weather delay can push crew into illegality, which cancels the next flight, which damages reputation, which affects tomorrow's slot negotiation.

Plan buffers. Watch the radar.

---

# TECHNOLOGY SYSTEMS

## Available Technologies

### CrewWise -$18,000,000
Predicts crew legality violations 2-6 hours ahead.
**Priority: High** -Crew issues will kill you without this.

### WebLogic -$22,000,000
Eliminates deadheading (crew flying as passengers to reposition).
**Priority: Medium** -Major cost saver at scale.

### Crew Rail -Infrastructure
Uses your E175s as 24/7 crew shuttles between bases.
**Priority: High** -Requires keeping your starter aircraft.

### HRD (Historical Route Data)
Intelligence on route performance before you commit.
**Priority: Medium** -Reduces guesswork on new routes.

### FBO (Tarmac Operations)
Improves turnaround times and ground operations.
**Priority: Low** -Nice to have, not essential.

### YieldMaster
Advanced dynamic pricing optimization.
**Priority: Medium** -Maximizes revenue per seat.

## When to Buy

Don't buy everything immediately. Prioritize:
1. **CrewWise** -First major purchase
2. **WebLogic** -When you have multiple crew bases
3. **Others** -As needed and affordable

---

# FINANCES

## Revenue Streams

- **Passenger tickets** -Main revenue
- **Cargo** -Secondary income
- **Ancillary fees** -Seat selection, bags, upgrades

## Cost Structure

- **Fuel** -Variable, major cost
- **Crew** -Salaries, training
- **Aircraft** -Lease payments or depreciation
- **Maintenance** -Scheduled and unscheduled
- **Airport fees** -Landing, gates, handling
- **Technology** -One-time and ongoing costs

## Cash Flow

You can be "profitable" on paper and still die from negative cash flow.

**Watch:**
- Lease payment timing
- Fuel cost spikes
- Unexpected maintenance
- Slot acquisition costs

**Rule:** Always maintain cash reserves. The unexpected will happen.

---

# ELITE OPERATOR STATUS (EOS)

## What Is EOS?

The ultimate achievement. Proof you've mastered airline operations.

## How to Qualify

You must meet ALL of these requirements for **7 consecutive days**:

| Requirement | Threshold |
|-------------|-----------|
| Reputation | ≥ 15,000 (Tier 5) |
| On-Time Performance | ≥ 85% (rolling 7-day) |
| Cancellations | Zero that day |
| Safety Incidents | Zero that day |
| Cash Flow | Positive |

## The 10-Day Challenge

If you fail any requirement on any day, your counter resets to zero. You must string together 10 consecutive perfect days.

This is not easy. Weather, mechanical issues, crew problems -anything can break your streak.

## EOS Is Permanent

Once you earn EOS, **you keep it forever**. Taking a break from the game doesn't affect your status. You earned it, it's yours.

## What EOS Unlocks

- Access to the **EOS Store**
- Ability to purchase the **Gold A380**
- Each Gold A380 purchase triggers a **collectible aircraft roll**

---

# THE GOLD A380 & COLLECTIBLES

## Two Different A380s

### Regular A380
- Standard widebody aircraft
- 500+ passengers
- Long-haul workhorse
- Available for lease or purchase like any other aircraft

### Gold A380
- **Only available in the EOS Store**
- **Only purchasable after earning EOS**
- Each purchase triggers a **roll on the collectible table**
- You're not buying a plane -you're buying a chance

## The Roll System

When you buy a Gold A380:
1. The purchase is processed
2. A random roll determines your reward
3. You receive a collectible aircraft based on rarity odds

## Rarity Tiers & Odds

| Rarity | Probability | Aircraft Count |
|--------|-------------|----------------|
| RARE | 79.9% | 20 |
| RELIC | 10% | 21 |
| ANOMALOUS | 5% | 24 |
| LEGENDARY | 5% | 25 (15 Legendary + 10 Ascendent) |
| MYTHIC | 0.1% | 1 |

## The Chase

Most rolls give you a Rare. Sometimes you'll get a Relic or Anomalous. Legendary is exciting.

And then there's the **Mythic**.

---

# COLLECTIBLE TIERS

## How Rarity Works

Each Gold A380 purchase triggers a roll against these odds:

| Tier | Drop Rate | Pool Size | What You Get |
|------|-----------|-----------|--------------|
| **RARE** | 79.9% | 20 aircraft | Premium variants, military transports |
| **RELIC** | 10% | 21 aircraft | Aviation history (1903-1980s) |
| **ANOMALOUS** | 5% | 24 aircraft | Rule-breaking physics, unique effects |
| **LEGENDARY** | 5% | 25 aircraft | Pinnacle engineering, cancelled dreams |
| **MYTHIC** | 0.1% | 1 aircraft | Server-limited ultra-rare |

## Tier Breakdown

### RARE (79.9%)
Premium variants and military transports. Good aircraft, solid additions to your museum. This is what you'll get most of the time.

### RELIC (10%)
Historic aircraft from aviation's golden age. Legends of the sky preserved in your collection.

### ANOMALOUS (5%)
Strange aircraft that break the rules of physics. Each has unique properties and bizarre behaviors.

### LEGENDARY (5%)
Pinnacle engineering achievements and cancelled dreams brought to life. Includes a sub-pool of "Ascendent" aircraft.

### MYTHIC (0.1%)
*"It officially doesn't exist. You didn't see this. None of this is happening."*

One per server per month. Massive rewards. **There can only be one.**

---

# TIPS & STRATEGY

## Early Game

1. **Lease E175s** -Don't buy anything yet
2. **Build regional routes** -Prove demand close to home
3. **Maintain cash reserves** -Things will go wrong
4. **Focus on on-time performance** -Reputation is everything
5. **Don't expand too fast** -Grow sustainably

## Mid Game

1. **Buy CrewWise** -Your first major purchase
2. **Expand concentrically** -Win nearby slots before reaching far
3. **Start banking flights** -Build hub connection economics
4. **Consider narrowbody aircraft** -Graduate from regionals
5. **Watch your OTP** -It affects everything

## Late Game

1. **Multi-hub strategy** -Spread your network
2. **Brand cohesion** -Single-carrier itineraries command premiums
3. **WebLogic** -Eliminate deadheading costs
4. **Push for Tier 5** -EOS is within reach
5. **Maintain reserves** -One bad week shouldn't ruin years of work

## The EOS Push

1. **Hit Tier 5 first** -Can't start the 10-day clock otherwise
2. **Build schedule buffers** -Weather will test you
3. **CrewWise is mandatory** -One crew violation resets everything
4. **Watch cash flow daily** -Positive every single day
5. **No cancellations** -Delay if you must, never cancel
6. **Pray for good weather** -Some things you can't control

## Collectible Strategy

1. **EOS first** -You can't roll without it
2. **Gold A380s aren't cheap** -Budget for multiple purchases
3. **Expect Rares** -80% of rolls are Rare tier
4. **Anomalous are fun** -Weird aircraft with weird properties
5. **Legendary is the realistic goal** -5% chance adds up over time
6. **Mythic is a dream** -0.1% means maybe never, maybe tomorrow

---

# FINAL NOTES

This is a simulator. Things go wrong. Weather happens. Crews get sick. Aircraft break. That's not the game being unfair -that's the game being realistic.

The airlines that succeed are the ones that plan for disruption, maintain reserves, and make smart decisions under pressure.

Good luck. The sky is waiting.

---

*Knowledge Base v1.0 -December 2024*
`;

// Simple markdown-to-JSX renderer
function renderMarkdown(text) {
  const lines = text.split('\n');
  const elements = [];
  let inTable = false;
  let tableRows = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Table handling
    if (line.startsWith('|') && line.endsWith('|')) {
      if (!inTable) {
        inTable = true;
        tableRows = [];
      }
      // Skip separator rows
      if (!line.includes('---')) {
        tableRows.push(line);
      }
      continue;
    } else if (inTable) {
      // End of table
      elements.push(
        <div key={i} className="overflow-x-auto my-4">
          <table className="min-w-full border-collapse" style={{ borderColor: '#333' }}>
            <thead>
              <tr style={{ backgroundColor: '#1a1a1a' }}>
                {tableRows[0]?.split('|').filter(c => c.trim()).map((cell, ci) => (
                  <th key={ci} className="px-4 py-2 text-left border" style={{ borderColor: '#333', color: '#60ffd0' }}>
                    {cell.trim()}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.slice(1).map((row, ri) => (
                <tr key={ri} style={{ backgroundColor: ri % 2 === 0 ? '#0c0c0c' : '#111' }}>
                  {row.split('|').filter(c => c.trim()).map((cell, ci) => (
                    <td key={ci} className="px-4 py-2 border" style={{ borderColor: '#333', color: '#e5e5e5' }}>
                      {cell.trim()}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      inTable = false;
      tableRows = [];
    }

    // Headers
    if (line.startsWith('# ')) {
      elements.push(<h1 key={i} className="text-3xl font-bold mt-8 mb-4" style={{ color: '#60ffd0' }}>{line.slice(2)}</h1>);
    } else if (line.startsWith('## ')) {
      elements.push(<h2 key={i} className="text-2xl font-bold mt-6 mb-3" style={{ color: '#60ffd0' }}>{line.slice(3)}</h2>);
    } else if (line.startsWith('### ')) {
      elements.push(<h3 key={i} className="text-xl font-bold mt-4 mb-2" style={{ color: '#22d3ee' }}>{line.slice(4)}</h3>);
    }
    // Horizontal rule
    else if (line === '---') {
      elements.push(<hr key={i} className="my-6" style={{ borderColor: '#333' }} />);
    }
    // List items
    else if (line.startsWith('- ')) {
      const content = line.slice(2);
      // Handle bold text
      const formatted = content.split(/(\*\*[^*]+\*\*)/).map((part, pi) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={pi} style={{ color: '#ffffff' }}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });
      elements.push(
        <li key={i} className="ml-6 mb-1" style={{ color: '#a1a1a1' }}>
          {formatted}
        </li>
      );
    }
    // Numbered list items
    else if (/^\d+\.\s/.test(line)) {
      const content = line.replace(/^\d+\.\s/, '');
      const formatted = content.split(/(\*\*[^*]+\*\*)/).map((part, pi) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={pi} style={{ color: '#ffffff' }}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });
      elements.push(
        <li key={i} className="ml-6 mb-1 list-decimal" style={{ color: '#a1a1a1' }}>
          {formatted}
        </li>
      );
    }
    // Paragraphs with bold/italic
    else if (line.trim()) {
      const formatted = line.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/).map((part, pi) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={pi} style={{ color: '#ffffff' }}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('*') && part.endsWith('*') && !part.startsWith('**')) {
          return <em key={pi} style={{ color: '#a1a1a1' }}>{part.slice(1, -1)}</em>;
        }
        return part;
      });
      elements.push(
        <p key={i} className="mb-2" style={{ color: '#a1a1a1' }}>
          {formatted}
        </p>
      );
    }
  }

  return elements;
}

export default function KnowledgeBase() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Get localized KB content based on current language
  const kbContent = getKBContent(i18n.language);

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: "#0a0a0a", color: "#e5e5e5" }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/opscenter")}
              style={{ color: "#737373" }}
            >
              &lt; {t('common.backToOpsCenter', 'Back to Ops Center')}
            </Button>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold" style={{ color: "#8B4513" }}>{t('pages.knowledgeBase.title', 'Knowledge Base')}</h1>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-invert max-w-none">
          {renderMarkdown(kbContent)}
        </div>

        {/* Footer */}
        <div className="mt-12 pt-6 border-t text-center" style={{ borderColor: '#333' }}>
          <Button
            onClick={() => navigate("/opscenter")}
            style={{ backgroundColor: "#8B4513", color: "#fff" }}
          >
            {t('common.returnToOperations', 'Return to Operations')}
          </Button>
        </div>
      </div>
    </div>
  );
}
